import useBooks from "api/BookResource/useBooks";
import useAuthStore from "store/authStore";
import useCheckIfUserIsSubscribed from "api/SubscriptionsResource/useCheckIfUserIsSubscribed";
import { BookListItem, CustomPaginator } from "components";
import { Container } from "layouts";
import PaddingX from "layouts/PaddingX";
import { useMemo, useState } from "react";
import BooksFilter from "./BooksFilter";
import useBooksFilters from "./useBooksFilters";
import useFetchCategories from "api/CategoryResource/useFetchCategories";
import useFetchPublishers from "api/PublisherResource/useFetchPublishers";
import useGenres from "api/GenreResource/useGenres";
import { IoFilterOutline, IoSearchOutline } from "react-icons/io5";
import HorizontalBookFilter from "./HorizontalBookFilter";
import { deleteEmptyObjects } from "utils";
import BooksPageLoader from "./BooksPageLoader";
import { motion, AnimatePresence } from "framer-motion";

const Books = () => {
	const filters = useBooksFilters();
	const loggedIn = useAuthStore((state) => state.loggedIn);
	const [searchValue, setSearchValue] = useState(filters.searchTerm || "");
	const [showFiltersForm, setShowFiltersForm] = useState(false);
	// const [focusSearchInput, setFocusSearchInput] = useState(false);

	// useEffect(() => {
	// 	if (filters?.openFilters === "true") {
	// 		setShowFiltersForm(true);
	// 	}

	// 	return () => {
	// 		setShowFiltersForm(false);
	// 		setFocusSearchInput(false);
	// 	};
	// }, [filters.focusSearchInput]);

	const handleSearch = (e) => {
		e.preventDefault();
		filters.changeSearchTerm(searchValue);
	};

	const handleOpenFilters = () => {
		setShowFiltersForm(true);
		filters.changeOpenFilters("true");
	};

	const countNumberOfFiltersSet = useMemo(
		() =>
			[
				filters.searchTerm,
				filters.categoryIs,
				filters.genreIs,
				filters.publisherIs,
				filters.pricingModelIs,
				filters.countryIs,
				filters.publicationDateFrom,
				filters.publicationDateTo,
				filters.priceMin,
				filters.priceMax,
				filters.format,
				filters.availability,
				filters.pageCountMin,
				filters.pageCountMax,
				filters.language,
			].filter((f) => f !== "").length,
		[filters],
	);

	const { data: isSubscribed, isLoading: isLoadingSubscribed } = useCheckIfUserIsSubscribed();
	const { data: categories, isLoading: isLoadingCategories } = useFetchCategories();
	const { data: publishers, isLoading: isLoadingPublishers } = useFetchPublishers();
	const { data: genres, isLoading: isLoadingGenres } = useGenres();

	const { data: books, isLoading: isLoadingBooks } = useBooks({
		page: filters.page,
		rows_per_page: filters.rowsPerPage,
		"filter[category_is]": filters.categoryIs,
		"filter[genre_is]": filters.genreIs,
		"filter[publisher_is]": filters.publisherIs,
		"filter[pricing_model_is]": filters.pricingModelIs,
		"filter[country_is]": filters.countryIs,
		"filter[publication_date_from]": filters.publicationDateFrom,
		"filter[publication_date_to]": filters.publicationDateTo,
		"filter[price_min]": filters.priceMin,
		"filter[price_max]": filters.priceMax,
		"filter[format]": filters.format,
		"filter[availability]": filters.availability,
		"filter[page_count_min]": filters.pageCountMin,
		"filter[page_count_max]": filters.pageCountMax,
		"filter[language_is]": filters.language,
		searchTerm: filters.searchTerm,
	});

	const disableRowsPerPageChanger = useMemo(() => {
		if (
			parseInt(filters?.page) === parseInt(books?.data?.total_pages) &&
			parseInt(filters?.page) !== 1
		) {
			return true;
		}
		return false;
	}, [filters?.page, books]);

	const handleChangeRowsPerPage = (value) => {
		if (value === filters?.rowsPerPage) return;
		filters.changeRowsPerPage(value);
	};

	const handleClearFilters = () => {
		const params = {
			first_page: "",
			rows: "",
			page: "",
			category_is: "",
			genre_is: "",
			publisher_is: "",
			pricing_model_is: "",
			country_is: "",
			search_term: "",
			publication_date_from: "",
			publication_date_to: "",
			price_min: "",
			price_max: "",
			format: "",
			availability: "",
			page_count_min: "",
			page_count_max: "",
			language: "",
			open_filters: "",
		};

		const queryParams = deleteEmptyObjects(params);

		filters.changeAllFiltersAtOnce(queryParams);
	};
	const isLoading =
		isLoadingBooks ||
		isLoadingSubscribed ||
		isLoadingCategories ||
		isLoadingPublishers ||
		isLoadingGenres;

	if (isLoading) {
		return <BooksPageLoader />;
	}

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.1,
			},
		},
	};

	const itemVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				duration: 0.3,
			},
		},
	};

	return (
		<Container className="flex flex-col w-full min-h-screen">
			<div className="relative overflow-hidden bg-gradient-to-br from-akgreener via-akgreener to-akgreenest mb-6">
				<PaddingX>
					<div className="relative py-8 sm:py-12">
						<div className="mx-auto max-w-4xl text-center">
							<h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
								Discover Your Next Great Read
							</h1>
							<p className="mt-3 text-base leading-7 text-gray-200 sm:mt-4">
								Search through our collection of books and find your perfect match.
							</p>
							<form onSubmit={handleSearch} className="mt-6 sm:mt-8">
								<div className="relative max-w-2xl mx-auto">
									<input
										type="text"
										value={searchValue}
										onChange={(e) => setSearchValue(e.target.value)}
										name="search"
										autoFocus={true}
										placeholder="Search for books by title, author, or ISBN..."
										className="block w-full rounded-full border-0 px-6 py-3 pl-12 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-akgreen text-base"
									/>
									<div className="absolute inset-y-0 left-0 flex items-center pl-4">
										<IoSearchOutline className="h-5 w-5 text-gray-400" />
									</div>
									<motion.button
										whileHover={{ scale: 1.02 }}
										whileTap={{ scale: 0.98 }}
										type="submit"
										className="absolute inset-y-1.5 right-1.5 flex items-center px-4 py-1.5 bg-akgreenest text-white rounded-full hover:bg-akgreener focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-akgreen transition-colors duration-200"
									>
										Search
									</motion.button>
								</div>
								{books?.data?.total_records > 0 && (
									<p className="mt-2 text-sm text-gray-300">
										Showing {books?.data?.records?.length} of {books?.data?.total_records} books
										available
									</p>
								)}
							</form>
						</div>
					</div>
				</PaddingX>
			</div>

			<PaddingX>
				<motion.div
					initial={{ opacity: 0, y: -20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.4 }}
					className="sticky top-0 z-10  pt-3"
				>
					{/* Mobile View */}
					<div className="md:hidden flex items-center justify-between gap-3" id="search_input">
						<div className="flex items-center gap-2">
							<select
								name="rowsPerPage"
								id="rowsPerPage"
								value={filters.rowsPerPage}
								disabled={disableRowsPerPageChanger}
								onChange={(e) => handleChangeRowsPerPage(e.target.value)}
								className="border border-gray-200 bg-white rounded-lg h-9 px-2 text-sm text-gray-700 focus:ring-1 focus:ring-akgreen focus:border-akgreen transition-all duration-200"
							>
								<option value="10">10</option>
								<option value="20">20</option>
								<option value="40">40</option>
								<option value="50">50</option>
							</select>
							<span className="text-sm text-gray-600">per page</span>
						</div>

						<div className="flex items-center gap-2">
							{countNumberOfFiltersSet > 0 && (
								<motion.button
									whileHover={{ scale: 1.02 }}
									whileTap={{ scale: 0.98 }}
									type="button"
									className="text-sm py-1.5 border border-red-300 bg-red-50 hover:bg-red-100 rounded-lg px-3 text-red-600 flex items-center transition-colors duration-200"
									onClick={handleClearFilters}
								>
									Clear ({countNumberOfFiltersSet})
								</motion.button>
							)}

							<motion.button
								whileHover={{ scale: 1.02 }}
								whileTap={{ scale: 0.98 }}
								className="flex items-center gap-2 bg-akgreen-50 hover:bg-akgreen-100 px-3 py-1.5 rounded-lg border border-akgreen-200 text-akgreen-700 transition-colors duration-200"
								type="button"
								onClick={handleOpenFilters}
							>
								{countNumberOfFiltersSet > 0 && (
									<motion.span
										initial={{ scale: 0 }}
										animate={{ scale: 1 }}
										className="absolute -top-2 -right-1 rounded-full flex items-center justify-center h-5 w-5 bg-akgreen-600 text-white text-xs font-medium"
									>
										{countNumberOfFiltersSet}
									</motion.span>
								)}
								<span className="text-sm">Filters</span>
								<IoFilterOutline size={16} />
							</motion.button>
						</div>
					</div>

					{/* Desktop View */}
					<div className="hidden md:flex flex-wrap items-center gap-3">
						<div className="flex-1">
							<HorizontalBookFilter
								publishers={publishers?.data}
								categories={categories?.data}
								genres={genres?.data?.data}
								filters={filters}
							/>
						</div>

						<div className="flex items-center gap-3 ml-auto">
							<div className="flex items-center gap-2">
								<select
									name="rowsPerPage"
									id="rowsPerPage"
									value={filters.rowsPerPage}
									disabled={disableRowsPerPageChanger}
									onChange={(e) => handleChangeRowsPerPage(e.target.value)}
									className="border border-gray-200 bg-white rounded-lg h-10 px-2 text-sm text-gray-700 focus:ring-1 focus:ring-akgreen focus:border-akgreen transition-all duration-200"
								>
									<option value="10">10 per page</option>
									<option value="20">20 per page</option>
									<option value="40">40 per page</option>
									<option value="50">50 per page</option>
								</select>
							</div>

							{countNumberOfFiltersSet > 0 && (
								<motion.button
									whileHover={{ scale: 1.02 }}
									whileTap={{ scale: 0.98 }}
									type="button"
									className="text-sm py-1.5 border border-red-300 bg-red-50 hover:bg-red-100 rounded-lg px-4 text-red-600 flex items-center transition-colors duration-200 whitespace-nowrap"
									onClick={handleClearFilters}
								>
									Clear filters ({countNumberOfFiltersSet})
								</motion.button>
							)}

							<motion.button
								whileHover={{ scale: 1.02 }}
								whileTap={{ scale: 0.98 }}
								className="text-sm flex items-center gap-2 bg-akgreen-50 hover:bg-akgreen-100 px-4 py-2 rounded-lg border border-akgreen-200 text-akgreen-700 transition-colors duration-200 whitespace-nowrap"
								type="button"
								onClick={handleOpenFilters}
							>
								<span>Advanced filters</span>
								<IoFilterOutline size={16} />
							</motion.button>
						</div>
					</div>
				</motion.div>

				<div className=" rounded-xl mt-6 pt-6 pb-8">
					<motion.div
						variants={containerVariants}
						initial="hidden"
						animate="visible"
						className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-5 gap-6"
					>
						{books?.data?.records
							?.sort((book1, book2) => book2.rank - book1.rank)
							?.map((book, index) => (
								<motion.div
									key={book.id}
									variants={itemVariants}
									className="overflow-hidden rounded-xl"
								>
									<BookListItem
										coverUrl={book?.book_cover?.url}
										title={book?.title}
										description={book?.description}
										price={book?.price}
										zwl_price={book?.zwl_price}
										credits={book.credits}
										publisher={
											book?.publisher || `${book?.user?.first_name} ${book?.user?.last_name}`
										}
										author={book?.author}
										reviews={book.ratings}
										bookId={book.id}
										sellable={book.sellable}
										rating={book.rating}
										isbn={book.rating}
										isPurchased={book.is_purchased}
										inLibrary={book.in_library}
										subscription={book.subscription}
										hasSubscription={isSubscribed?.data}
										category={book?.category?.name}
										isFree={book?.isFree}
										format={book?.format}
										genres={book?.genre?.map((genre) => [genre.id, genre.name])}
										showWishlist={loggedIn}
										isInWishlist={book?.in_wishlist}
										hasPreview={book?.has_preview}
										// showReadOnAppBadge={true}
									/>
								</motion.div>
							))}
					</motion.div>
				</div>

				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ delay: 0.2 }}
					className="flex-1 flex items-end mt-6 border-t pt-6"
				>
					<CustomPaginator
						firstPage={filters.firstPage}
						setFirstPage={filters.changeFirstPage}
						rowsPerPage={filters.rowsPerPage}
						setRowsPerPage={filters.changeRowsPerPage}
						totalRecords={books?.data?.total_records}
						setPage={filters.changePage}
						showRowsPerPageDropdown={false}
					/>
				</motion.div>

				<AnimatePresence>
					{showFiltersForm && (
						<BooksFilter
							visible={showFiltersForm}
							setVisible={setShowFiltersForm}
							publishers={publishers?.data}
							categories={categories?.data}
							genres={genres?.data?.data}
							filters={filters}
							countNumberOfFiltersSet={countNumberOfFiltersSet}
						/>
					)}
				</AnimatePresence>
			</PaddingX>
		</Container>
	);
};

export default Books;
